import { VStack } from '@genial/components';
import { Color, Spacing } from '@genial/design-tokens';
import { Box } from 'native-base';
import React from 'react';

import { ColorCodedTextProps } from '../../../../../../../components/ColorCodedText';
import {
  RecommendedPortfolioBadge,
  RecommendedPortfolioBadgeProps,
} from '../../../../../../../components/RecommendedPortfolioBadge';
import { IInvestedAssetsNormalized } from '../../../../../../../store/customerDetail/types';
import { QuoteButton, QuoteButtonProps } from '../../../../QuoteButton';
import { NotFoundTable, ProductFieldProps, messageError } from '../../constants';

import { AssetItem } from './components/AssetItem';
import { AssetsList } from './components/AssetsList';
import { ProductTabletCard } from './components/Card';
import { ProductTabletCardTop } from './components/CardTop';

export interface ProductTableTabletProps<T> {
  data: T[];
  isLoading: boolean;
  fields: ProductFieldProps<T>[];
  secondaryInfo?: React.FC<{ product: T }>;
  columns: number;
  rows: number;
  product: IInvestedAssetsNormalized;
  quoteButtonInfoGetter?: (item: T) => QuoteButtonProps;
  recommendedPortfolioInfoGetter?: (item: T) => RecommendedPortfolioBadgeProps;
}

export const ProductTableTablet = <T,>({
  data,
  product,
  fields,
  isLoading,
  secondaryInfo: SecondaryInfo,
  columns,
  rows,
  quoteButtonInfoGetter,
  recommendedPortfolioInfoGetter,
}: ProductTableTabletProps<T>) => {
  const mainInfo = fields[0].key;
  const otherFields = fields.slice(1);
  const hasQuoteButton = !!quoteButtonInfoGetter;
  const hasRecommendedPortfolioBadge = !!recommendedPortfolioInfoGetter;

  const [isOpen, setIsOpen] = React.useState<number[]>([]);

  const getColoredField = (
    item: T,
    field: ProductFieldProps<T>
  ): ColorCodedTextProps['sign'] | undefined => {
    if (field.coloredField) {
      return item[field.coloredField] as ColorCodedTextProps['sign'];
    }
    return undefined;
  };

  const renderItem = (
    label: string,
    value: string | number = messageError,
    coloredBySign?: ColorCodedTextProps['sign'],
    highlighted?: boolean
  ) => (
    <AssetItem
      columns={columns}
      isLoading={isLoading}
      label={label}
      rows={rows}
      value={`${value}`}
      coloredBySign={coloredBySign}
      highlighted={highlighted}
    />
  );

  const renderLoadingItem = (label: string) => (
    <AssetItem columns={columns} isLoading={isLoading} label={label} rows={rows} />
  );

  const renderQuoteButton = (item: T) => {
    if (!hasQuoteButton) {
      return null;
    }
    const quoteButtonInfo = quoteButtonInfoGetter(item);

    return (
      <QuoteButton {...quoteButtonInfo} customProps={{ p: 0, ...quoteButtonInfo.customProps }} />
    );
  };

  const renderRecommendedPortfolioBadge = (item: T) => {
    if (!hasRecommendedPortfolioBadge) {
      return null;
    }
    const recommendedPortfolioInfo = recommendedPortfolioInfoGetter(item);

    return <RecommendedPortfolioBadge {...recommendedPortfolioInfo} />;
  };

  const renderProductItem = (item: T, index: number) => (
    <ProductTabletCard index={index} product={product}>
      <ProductTabletCardTop
        hasSecondaryInfo={!!SecondaryInfo}
        customHeaderButton={renderQuoteButton(item)}
        customRecommendedPortfolioBadge={renderRecommendedPortfolioBadge(item)}
        isOpenSecondaryInfo={isOpen.includes(index)}
        mainInfo={`${item[mainInfo]}`}
        onPressSecondaryInfo={() =>
          setIsOpen(
            isOpen.includes(index) ? [...isOpen.filter((i) => index !== i)] : [...isOpen, index]
          )
        }
      />
      <Box w="full" h="1px" bgColor={Color['neutral.lightest']} />
      <AssetsList
        id="flatList-dynamicTable-detail"
        keyExtractor={(field) => `${field.label}`}
        data={otherFields}
        renderItem={({ item: field }) =>
          renderItem(
            field.label,
            item[field.key] as string | number,
            getColoredField(item, field),
            field.highlighted
          )
        }
        columns={columns}
        rows={rows}
      />
      {isOpen.includes(index) && !!SecondaryInfo && <SecondaryInfo product={item} />}
    </ProductTabletCard>
  );

  const renderProductItemLoading = (index: number) => (
    <ProductTabletCard index={index} product={product}>
      <ProductTabletCardTop
        hasSecondaryInfo={!!SecondaryInfo}
        isOpenSecondaryInfo={isOpen.includes(index)}
        isLoading
      />
      <Box w="full" h="1px" bgColor={Color['neutral.lightest']} />
      <AssetsList
        id="flatList-dynamicTable-detail"
        keyExtractor={(field) => `${field.label}`}
        data={otherFields}
        renderItem={({ item: field }) => renderLoadingItem(field.label)}
        columns={columns}
        rows={rows}
      />
    </ProductTabletCard>
  );

  if (isLoading) {
    return (
      <VStack space={Spacing['stack.xxxs']}>
        {Array.from({ length: 3 }).map((_, index) => renderProductItemLoading(index))}
      </VStack>
    );
  }

  if (data?.length === 0 && !isLoading) {
    return <NotFoundTable />;
  }

  return (
    <VStack space={Spacing['stack.xxxs']} pb={Spacing['stack.xxxs']}>
      {data.map((item, index) => renderProductItem(item, index))}
    </VStack>
  );
};
